import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertCircle,
  TrendingDown,
  TrendingUp,
  RefreshCw,
  Clock,
  Edit,
  AlertTriangle,
  Database,
  Plus,
} from "lucide-react";
import { ProgressWithIndicator } from "@/components/ui/progress-with-indicator";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  updateInventoryQuantity,
  getInventoryItems,
} from "@/services/inventory/inventoryService";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { useTranslation } from "react-i18next";

interface InventoryItem {
  id: string;
  name: string;
  description?: string;
  current_quantity: number;
  minimum_quantity: number;
  unit_of_measure: string;
  store_id: string;
  inventory_type: string;
  created_at: string;
  updated_at: string;
}

interface MenuItem {
  id: string;
  name: string;
  description: string;
  available: boolean;
  expected_quantity?: number;
  inventory_item_id?: string;
}

interface ManuallyTrackedItemsProps {
  storeId: string;
  isAdvancedMode: boolean;
  isLoading: boolean;
  onRefresh?: () => void;
  translate: (key: string) => string;
}

export function ManuallyTrackedItems({
  storeId,
  isAdvancedMode,
  isLoading,
  onRefresh,
  translate,
}: ManuallyTrackedItemsProps) {
  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="h-40 flex items-center justify-center">
          <p className="text-muted-foreground">
            {translate("inventory.manuallyTracked.loading")}
          </p>
        </div>
      </Card>
    );
  }

  // Return early if no storeId is provided
  if (!storeId) {
    return (
      <Card className="p-6">
        <div className="h-40 flex items-center justify-center">
          <p className="text-muted-foreground">
            {translate("inventory.manuallyTracked.selectStore")}
          </p>
        </div>
      </Card>
    );
  }

  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [quantityValues, setQuantityValues] = useState<Record<string, number>>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (storeId) {
      fetchManualInventoryItems();
      fetchManuallyTrackedItems();
    }
  }, [storeId]);

  const fetchManualInventoryItems = async () => {
    setLoading(true);
    try {
      // Get manual inventory items specifically
      const items = await getInventoryItems(storeId, "manual");
      setInventoryItems(items);
    } catch (error) {
      console.error("Error fetching manual inventory items:", error);
      toast({
        title: "Error",
        description: "Failed to load manual inventory items",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchManuallyTrackedItems = async () => {
    if (!storeId) return;

    setLoading(true);
    try {
      // Get menu items that have manual inventory relationships
      const { data: menuItems, error: menuError } = await supabase
        .from("menu_items")
        .select(
          `
          id, 
          name, 
          description, 
          available,
          menu_item_inventory!inner (
            inventory_item_id,
            quantity_required,
            inventory_items!inner (
              id,
              name,
              current_quantity,
              inventory_type
            )
          )
        `
        )
        .eq("store_id", storeId)
        .eq("menu_item_inventory.inventory_items.inventory_type", "manual");

      if (menuError) throw menuError;

      // Transform the data to include expected_quantity
      const transformedItems = (menuItems || []).map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        available: item.available,
        expected_quantity:
          item.menu_item_inventory?.[0]?.inventory_items?.current_quantity || 0,
        inventory_item_id: item.menu_item_inventory?.[0]?.inventory_items?.id,
      }));

      setMenuItems(transformedItems);

      // Initialize quantity values
      const quantities: Record<string, number> = {};
      transformedItems.forEach((item) => {
        quantities[item.id] = item.expected_quantity || 0;
      });
      setQuantityValues(quantities);
    } catch (error) {
      console.error("Error fetching manually tracked menu items:", error);
      toast({
        title: "Error",
        description: "Failed to load manually tracked items",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (itemId: string, value: string) => {
    const numValue = parseInt(value, 10) || 0;
    setQuantityValues((prev) => ({
      ...prev,
      [itemId]: numValue,
    }));
  };

  const handleStartEdit = (itemId: string) => {
    setEditingItem(itemId);
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  const handleSaveQuantity = async (itemId: string) => {
    try {
      const newQuantity = quantityValues[itemId];

      // Find the menu item to get its inventory_item_id
      const menuItem = menuItems.find((item) => item.id === itemId);
      if (!menuItem || !menuItem.inventory_item_id) {
        throw new Error("Could not find inventory item for this menu item");
      }

      // Update the inventory item quantity
      const result = await updateInventoryQuantity(
        menuItem.inventory_item_id,
        newQuantity
      );

      if (result.success) {
        toast({
          title: "Quantity Updated",
          description: "The inventory quantity has been updated successfully",
        });

        setEditingItem(null);
        fetchManuallyTrackedItems(); // Refresh the data
        fetchManualInventoryItems(); // Also refresh inventory items
      } else {
        throw new Error("Failed to update inventory");
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
      toast({
        title: "Update Failed",
        description: "Failed to update the quantity",
        variant: "destructive",
      });
    }
  };

  const getStockStatus = (current: number, minimum: number) => {
    const ratio = current / Math.max(minimum, 1);
    if (ratio <= 0)
      return {
        color: "destructive",
        label: "Out of Stock",
        indicator: "bg-destructive",
      };
    if (ratio < 1)
      return { color: "amber", label: "Low Stock", indicator: "bg-amber-500" };
    return { color: "green", label: "In Stock", indicator: "bg-green-500" };
  };

  const getProgressValue = (current: number, minimum: number) => {
    // Calculate percentage of current quantity relative to 2x the minimum for better visualization
    const target = minimum * 2;
    const percentage = Math.min(
      100,
      Math.round((current / Math.max(target, 1)) * 100)
    );
    return percentage;
  };

  const handleRefresh = () => {
    fetchManualInventoryItems();
    fetchManuallyTrackedItems();
    if (onRefresh) {
      onRefresh();
    }
  };

  // Process inventory data for the chart
  const getInventoryByCategory = () => {
    // Group items by category (using description field as category for demo)
    const categoryMap = new Map();

    inventoryItems.forEach((item) => {
      const category = item.description || "Uncategorized";
      if (!categoryMap.has(category)) {
        categoryMap.set(category, {
          name: category,
          current: 0,
          minimum: 0,
          count: 0,
        });
      }

      const categoryData = categoryMap.get(category);
      categoryData.current += item.current_quantity;
      categoryData.minimum += item.minimum_quantity;
      categoryData.count += 1;
    });

    // Convert map to array and sort by current quantity
    return Array.from(categoryMap.values())
      .sort((a, b) => b.current - a.current)
      .slice(0, 6); // Show top 6 categories
  };

  const chartData = getInventoryByCategory();

  // Get top categories by number of items
  const topCategories = Array.from(
    new Set(inventoryItems.map((item) => item.description || "Uncategorized"))
  ).slice(0, 5);

  if (loading) {
    return (
      <Card className="p-6">
        <div className="h-40 flex items-center justify-center">
          <p className="text-muted-foreground">
            Loading manually tracked items...
          </p>
        </div>
      </Card>
    );
  }

  // Calculate stats for alerts
  const lowStockItems = inventoryItems.filter(
    (item) =>
      item.current_quantity > 0 && item.current_quantity < item.minimum_quantity
  );
  const outOfStockItems = inventoryItems.filter(
    (item) => item.current_quantity <= 0
  );

  return (
    <div className="space-y-6">
      {/* Grid with Summary Cards */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        {/* Inventory Summary Card */}
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">Manual Inventory Summary</h3>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-muted rounded-lg p-4 text-center">
              <div className="text-4xl font-bold">{inventoryItems.length}</div>
              <div className="text-sm text-muted-foreground mt-1">
                Total Items
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4 text-center">
              <div className="text-4xl font-bold text-destructive">
                {outOfStockItems.length}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Out of Stock
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4 text-center">
              <div className="text-4xl font-bold text-amber-500">
                {lowStockItems.length}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Low Stock
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex justify-between items-center text-sm mb-2">
              <span>
                <span className="text-green-500 font-medium">
                  {inventoryItems.length -
                    lowStockItems.length -
                    outOfStockItems.length}
                </span>{" "}
                in stock
              </span>
              <span>
                <span className="text-amber-500 font-medium">
                  {lowStockItems.length}
                </span>{" "}
                low
              </span>
            </div>

            <div className="h-4 w-full bg-muted rounded-full overflow-hidden">
              {inventoryItems.length > 0 && (
                <>
                  <div
                    className="h-4 bg-green-500 float-left"
                    style={{
                      width: `${
                        ((inventoryItems.length -
                          lowStockItems.length -
                          outOfStockItems.length) /
                          inventoryItems.length) *
                        100
                      }%`,
                    }}
                  ></div>
                  <div
                    className="h-4 bg-amber-500 float-left"
                    style={{
                      width: `${
                        (lowStockItems.length / inventoryItems.length) * 100
                      }%`,
                    }}
                  ></div>
                  <div
                    className="h-4 bg-red-500 float-left"
                    style={{
                      width: `${
                        (outOfStockItems.length / inventoryItems.length) * 100
                      }%`,
                    }}
                  ></div>
                </>
              )}
            </div>

            <div className="text-sm mt-4">
              <span className="font-medium">Manual Inventory Items: </span>
              <span className="text-destructive">
                {outOfStockItems.length} critical
              </span>
              {" • "}
              <span className="text-amber-500">{lowStockItems.length} low</span>
              {" • "}
              <span className="text-green-500">
                {inventoryItems.length -
                  lowStockItems.length -
                  outOfStockItems.length}{" "}
                healthy
              </span>
            </div>
          </div>
        </Card>

        {/* Inventory Alerts Card */}
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">Manual Inventory Alerts</h3>

          {lowStockItems.length === 0 && outOfStockItems.length === 0 ? (
            <div className="h-40 flex flex-col items-center justify-center gap-2">
              <div className="rounded-full bg-green-100 p-3">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-muted-foreground">
                All manual inventory items are at healthy levels.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {outOfStockItems.map((item) => (
                <div key={item.id} className="rounded-md bg-destructive/10 p-3">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-destructive" />
                      <p className="font-medium text-destructive">
                        {item.name}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className="border-destructive text-destructive"
                    >
                      Out of Stock
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    0 / {item.minimum_quantity}{" "}
                    {item.unit_of_measure || "units"}
                  </p>
                </div>
              ))}

              {lowStockItems.map((item) => (
                <div key={item.id} className="rounded-md bg-amber-500/10 p-3">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="h-4 w-4 text-amber-500" />
                      <p className="font-medium text-amber-500">{item.name}</p>
                    </div>
                    <Badge
                      variant="outline"
                      className="border-amber-500 text-amber-500"
                    >
                      Low Stock
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {item.current_quantity} / {item.minimum_quantity}{" "}
                    {item.unit_of_measure || "units"} remaining
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Inventory by Category Card */}
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">
            Manual Inventory by Category
          </h3>

          {inventoryItems.length === 0 ? (
            <div className="h-40 flex items-center justify-center">
              <p className="text-muted-foreground">
                No manual inventory data available
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-1 mb-3">
                {topCategories.map((category) => (
                  <Badge key={category} variant="outline" className="text-xs">
                    {category}
                  </Badge>
                ))}
              </div>

              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={12} tickMargin={5} />
                    <YAxis fontSize={12} />
                    <Tooltip
                      formatter={(value, name) => [
                        value,
                        name === "current"
                          ? "Current Stock"
                          : "Minimum Required",
                      ]}
                      labelFormatter={(label) => `Category: ${label}`}
                    />
                    <Legend />
                    <Bar
                      dataKey="current"
                      name="Current Stock"
                      fill="#6366F1"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="minimum"
                      name="Minimum Required"
                      fill="#F43F5E"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Alerts Section */}
      {(lowStockItems.length > 0 || outOfStockItems.length > 0) && (
        <Card className="p-6 border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <h3 className="text-lg font-medium">Manual Inventory Alerts</h3>
          </div>

          <div className="space-y-3">
            {outOfStockItems.length > 0 && (
              <div className="rounded-md bg-destructive/10 p-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <p className="font-medium text-destructive">
                    {outOfStockItems.length} items are out of stock
                  </p>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {outOfStockItems.map((item) => item.name).join(", ")}
                </p>
              </div>
            )}

            {lowStockItems.length > 0 && (
              <div className="rounded-md bg-amber-500/10 p-3">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-amber-500" />
                  <p className="font-medium text-amber-500">
                    {lowStockItems.length} items are running low
                  </p>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {lowStockItems.map((item) => item.name).join(", ")}
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Inventory Items Table */}
      {/* <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Database className="h-5 w-5" />
            Manual Inventory Items
          </h3>

          <Button variant="outline" size="sm" onClick={refreshManualData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="overflow-x-auto">
          {inventoryItems.length === 0 ? (
            <div className="h-40 flex flex-col items-center justify-center text-center gap-2">
              <AlertCircle className="h-10 w-10 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                No manual inventory items found.
              </p>
              <p className="text-sm text-muted-foreground">
                Create manual inventory tracking from the menu item dialog to
                see items here.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Stock Level</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inventoryItems.map((item) => {
                  const stockStatus = getStockStatus(
                    item.current_quantity,
                    item.minimum_quantity
                  );
                  const progressValue = getProgressValue(
                    item.current_quantity,
                    item.minimum_quantity
                  );

                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>{item.description || "N/A"}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2 w-full max-w-[200px]">
                          <ProgressWithIndicator
                            value={progressValue}
                            className="h-2"
                            indicatorClassName={stockStatus.indicator}
                          />
                          <span className="text-xs">
                            {item.current_quantity} / {item.minimum_quantity}{" "}
                            {item.unit_of_measure || "units"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            stockStatus.color === "destructive"
                              ? "destructive"
                              : "outline"
                          }
                          className={`${
                            stockStatus.color === "amber"
                              ? "border-amber-500 text-amber-500"
                              : ""
                          } 
                                    ${
                                      stockStatus.color === "green"
                                        ? "border-green-500 text-green-500"
                                        : ""
                                    }`}
                        >
                          {stockStatus.label}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </Card> */}

      {/* Menu Items Table */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Manually Tracked Menu Items
          </h3>

          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Current Stock</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {menuItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <AlertCircle className="h-10 w-10 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground">
                        No manually tracked menu items found.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Set up manual inventory tracking for menu items to see
                        them here.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                menuItems.map((item) => {
                  const quantity = item.expected_quantity || 0;
                  const status = getStockStatus(quantity, 0);
                  const progressValue = getProgressValue(quantity, 5); // Using 5 as minimum for visualization
                  const isEditing = editingItem === item.id;

                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {item.description || "No description"}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              value={quantityValues[item.id]}
                              onChange={(e) =>
                                handleQuantityChange(item.id, e.target.value)
                              }
                              className="w-20"
                            />
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleSaveQuantity(item.id)}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2 w-full max-w-[200px]">
                            <ProgressWithIndicator
                              value={progressValue}
                              className="h-2"
                              indicatorClassName={status.indicator}
                            />
                            <span className="text-xs">
                              {quantity} {quantity === 1 ? "unit" : "units"}
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            status.color === "destructive"
                              ? "destructive"
                              : "outline"
                          }
                          className={`${
                            status.color === "amber"
                              ? "border-amber-500 text-amber-500"
                              : ""
                          } 
                                    ${
                                      status.color === "green"
                                        ? "border-green-500 text-green-500"
                                        : ""
                                    }`}
                        >
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {!isEditing && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleStartEdit(item.id)}
                          >
                            <Edit className="h-4 w-4 mr-1" /> Update Stock
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
